import React, { useState, useEffect } from 'react';
import {
  isWebAuthnSupported,
  isPlatformAuthenticatorAvailable,
  registerPasskey,
  getPasskeyCredentials,
  deletePasskeyCredential,
} from '../utils/webauthn';

/**
 * PasskeySetup Component
 *
 * Allows users to:
 * - Register new passkeys
 * - View existing passkeys
 * - Delete passkeys
 *
 * Note: Uses session cookie authentication via axios
 */
const PasskeySetup = ({ onClose }) => {
  const [isSupported, setIsSupported] = useState(false);
  const [isPlatformAvailable, setIsPlatformAvailable] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deviceName, setDeviceName] = useState('');

  useEffect(() => {
    checkSupport();
    loadCredentials();
  }, []);

  const checkSupport = async () => {
    const supported = isWebAuthnSupported();
    const platformAvailable = await isPlatformAuthenticatorAvailable();

    setIsSupported(supported);
    setIsPlatformAvailable(platformAvailable);
  };

  const loadCredentials = async () => {
    try {
      setLoading(true);
      const creds = await getPasskeyCredentials();
      setCredentials(creds);
    } catch (err) {
      console.error('Failed to load credentials:', err);
      setError('Failed to load passkeys');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegistering(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await registerPasskey(deviceName || null);
      setSuccess('Passkey registered successfully!');
      setDeviceName('');
      await loadCredentials();
    } catch (err) {
      console.error('Failed to register passkey:', err);
      if (err.name === 'NotAllowedError') {
        setError('Registration cancelled or timed out');
      } else if (err.name === 'InvalidStateError') {
        setError('This passkey is already registered');
      } else {
        setError(err.message || 'Failed to register passkey');
      }
    } finally {
      setRegistering(false);
    }
  };

  const handleDelete = async (credentialId) => {
    if (!window.confirm('Are you sure you want to delete this passkey?')) {
      return;
    }

    try {
      await deletePasskeyCredential(credentialId);
      setSuccess('Passkey deleted successfully');
      await loadCredentials();
    } catch (err) {
      console.error('Failed to delete passkey:', err);
      setError('Failed to delete passkey');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (!isSupported) {
    return (
      <div className="passkey-setup">
        <div className="passkey-setup-header">
          <h2>Passkey Setup</h2>
          {onClose && (
            <button onClick={onClose} className="close-button" aria-label="Close">
              ×
            </button>
          )}
        </div>
        <div className="error-message">
          <p>Your browser does not support passkeys.</p>
          <p>Please use a modern browser like Chrome, Safari, or Edge.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="passkey-setup">
      <div className="passkey-setup-header">
        <h2>Passkey Setup</h2>
        {onClose && (
          <button onClick={onClose} className="close-button" aria-label="Close">
            ×
          </button>
        )}
      </div>

      <div className="passkey-info">
        <p>
          Passkeys let you sign in using your fingerprint, face, or screen lock.
          They're more secure than passwords and work across your devices.
        </p>
        {!isPlatformAvailable && (
          <div className="warning-message">
            <p>
              Platform authenticator not detected. You may need to use a security key
              or enable biometric authentication on your device.
            </p>
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {success && (
        <div className="success-message">
          <p>{success}</p>
          <button onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      <div className="register-section">
        <h3>Register New Passkey</h3>
        <form onSubmit={handleRegister}>
          <div className="form-group">
            <label htmlFor="device-name">
              Device Name (Optional)
            </label>
            <input
              id="device-name"
              type="text"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder="e.g., My iPhone, Work Laptop"
              disabled={registering}
            />
            <small>Give this passkey a name to help you remember which device it's on</small>
          </div>
          <button
            type="submit"
            disabled={registering}
            className="register-button"
          >
            {registering ? 'Registering...' : 'Register Passkey'}
          </button>
        </form>
      </div>

      <div className="credentials-section">
        <h3>Your Passkeys</h3>
        {loading ? (
          <p>Loading passkeys...</p>
        ) : credentials.length === 0 ? (
          <p className="no-credentials">No passkeys registered yet</p>
        ) : (
          <div className="credentials-list">
            {credentials.map((cred) => (
              <div key={cred.id} className="credential-item">
                <div className="credential-info">
                  <div className="credential-name">
                    {cred.device_name || 'Unnamed Passkey'}
                  </div>
                  <div className="credential-details">
                    <span>Created: {formatDate(cred.created_at)}</span>
                    {cred.last_used_at && (
                      <span>Last used: {formatDate(cred.last_used_at)}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(cred.id)}
                  className="delete-button"
                  aria-label="Delete passkey"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .passkey-setup {
          max-width: 600px;
          margin: 0 auto;
          padding: 20px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }

        .passkey-setup-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 15px;
        }

        .passkey-setup-header h2 {
          margin: 0;
          font-size: 24px;
          font-weight: 600;
          color: #111827;
        }

        .close-button {
          background: none;
          border: none;
          font-size: 32px;
          cursor: pointer;
          color: #6b7280;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
        }

        .close-button:hover {
          color: #111827;
        }

        .passkey-info {
          margin-bottom: 20px;
          padding: 15px;
          background: #f3f4f6;
          border-radius: 6px;
        }

        .passkey-info p {
          margin: 0;
          color: #4b5563;
          line-height: 1.6;
        }

        .warning-message {
          margin-top: 10px;
          padding: 10px;
          background: #fef3c7;
          border-left: 4px solid #f59e0b;
          border-radius: 4px;
        }

        .warning-message p {
          margin: 0;
          color: #92400e;
          font-size: 14px;
        }

        .error-message {
          padding: 12px;
          background: #fee;
          border-left: 4px solid #ef4444;
          border-radius: 4px;
          margin-bottom: 15px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .error-message p {
          margin: 0;
          color: #991b1b;
        }

        .error-message button {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: #991b1b;
          padding: 0;
        }

        .success-message {
          padding: 12px;
          background: #d1fae5;
          border-left: 4px solid #10b981;
          border-radius: 4px;
          margin-bottom: 15px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .success-message p {
          margin: 0;
          color: #065f46;
        }

        .success-message button {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: #065f46;
          padding: 0;
        }

        .register-section {
          margin-bottom: 30px;
          padding-bottom: 30px;
          border-bottom: 1px solid #e5e7eb;
        }

        .register-section h3 {
          margin: 0 0 15px 0;
          font-size: 18px;
          font-weight: 600;
          color: #111827;
        }

        .form-group {
          margin-bottom: 15px;
        }

        .form-group label {
          display: block;
          margin-bottom: 5px;
          font-weight: 500;
          color: #374151;
        }

        .form-group input {
          width: 100%;
          padding: 10px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          box-sizing: border-box;
        }

        .form-group input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .form-group small {
          display: block;
          margin-top: 5px;
          color: #6b7280;
          font-size: 12px;
        }

        .register-button {
          width: 100%;
          padding: 12px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s;
        }

        .register-button:hover:not(:disabled) {
          background: #2563eb;
        }

        .register-button:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        .credentials-section h3 {
          margin: 0 0 15px 0;
          font-size: 18px;
          font-weight: 600;
          color: #111827;
        }

        .no-credentials {
          text-align: center;
          color: #6b7280;
          padding: 20px;
        }

        .credentials-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .credential-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: #f9fafb;
        }

        .credential-info {
          flex: 1;
        }

        .credential-name {
          font-weight: 500;
          color: #111827;
          margin-bottom: 5px;
        }

        .credential-details {
          display: flex;
          flex-direction: column;
          gap: 2px;
          font-size: 12px;
          color: #6b7280;
        }

        .delete-button {
          padding: 8px 16px;
          background: white;
          color: #ef4444;
          border: 1px solid #ef4444;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .delete-button:hover {
          background: #ef4444;
          color: white;
        }

        @media (max-width: 640px) {
          .passkey-setup {
            padding: 15px;
          }

          .credential-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .delete-button {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default PasskeySetup;
